/**
 * WorkspaceNav — left sidebar navigation for workspace files/folders.
 *
 * Fetches the full file list via a single recursive API call, then builds
 * a folder tree client-side by splitting each file key on '/'.
 * Clicking a folder toggles expand/collapse (no additional fetches).
 * Clicking a file notifies parent via onFileSelect.
 *
 * Recent section: shows most-recently-opened files (persisted in localStorage
 * by parent via useRecentFiles hook). Rendered above the file tree.
 *
 * Workspace link support: when refreshTrigger increments (from context),
 * re-fetches file list. When pendingNavigateKey is set, auto-expands
 * parent folders and selects the target file after load completes.
 */

import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  gatewayClient,
  WorkspaceFile,
} from '../../client/GatewayClient';
import { useWorkspaceNav } from '../../context/WorkspaceNavContext';
import { useImpersonation } from '../../context/ImpersonationContext';
import { useAgent } from '../../context/AgentContext';
import { RecentEntry } from '../../hooks/useRecentFiles';
import './WorkspaceNav.css';

// ── Types ───────────────────────────────────────────────────────────────────

interface TreeNode {
  /** Display name (folder or file name) */
  name: string;
  /** Full path (folder path ends with '/') */
  path: string;
  /** Whether this is a folder */
  isFolder: boolean;
  /** Children nodes */
  children: TreeNode[];
  /** Original file metadata (for files only) */
  file?: WorkspaceFile;
}

interface WorkspaceNavProps {
  onFileSelect: (file: WorkspaceFile) => void;
  selectedFileKey?: string;
  isCollapsed?: boolean;
  onToggle?: () => void;
  /** Recently opened files (from useRecentFiles hook) */
  recentFiles?: RecentEntry[];
  /** Remove a stale recent entry (file no longer in workspace) */
  onRemoveRecent?: (key: string) => void;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Build a hierarchical tree from a flat list of files by splitting each
 * file's key on '/'. Intermediate segments become folder nodes.
 */
/** Error-state marker for a non-Error failure; translated at render so it follows the locale. */
const LOAD_FAILED = Symbol('loadFailed');

function buildTree(files: WorkspaceFile[]): TreeNode[] {
  const root: TreeNode[] = [];
  const folderMap = new Map<string, TreeNode>();

  for (const file of files) {
    const segments = file.key.split('/');
    let current = root;

    // Walk/create folder nodes for all segments except the last (the file name)
    for (let i = 0; i < segments.length - 1; i++) {
      const folderPath = segments.slice(0, i + 1).join('/') + '/';
      let folder = folderMap.get(folderPath);
      if (!folder) {
        folder = {
          name: segments[i],
          path: folderPath,
          isFolder: true,
          children: [],
        };
        folderMap.set(folderPath, folder);
        current.push(folder);
      }
      current = folder.children;
    }

    // Add the file node
    const fileName = segments[segments.length - 1];
    current.push({
      name: fileName,
      path: file.key,
      isFolder: false,
      children: [],
      file,
    });
  }

  return root;
}

/** Sort tree: folders first (alphabetical), then files (alphabetical). */
function sortTree(nodes: TreeNode[]): TreeNode[] {
  const sorted = [...nodes].sort((a, b) => {
    if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  for (const node of sorted) {
    if (node.isFolder && node.children.length > 0) {
      node.children = sortTree(node.children);
    }
  }
  return sorted;
}

/**
 * Return a pruned copy of the tree containing only nodes whose name matches
 * the query or folders that have matching descendants (with children pruned).
 * Work is done once; callers can iterate node.children directly.
 */
function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
  if (!query) return nodes;
  const lower = query.toLowerCase();
  return nodes.reduce<TreeNode[]>((acc, node) => {
    // Name matches → include node (and all descendants) as-is
    if (node.name.toLowerCase().includes(lower)) {
      acc.push(node);
      return acc;
    }
    // Folder doesn't match → keep only if a descendant matches
    if (node.isFolder) {
      const filteredChildren = filterTree(node.children, query);
      if (filteredChildren.length > 0) {
        acc.push({ ...node, children: filteredChildren });
      }
    }
    return acc;
  }, []);
}

// ── Component ───────────────────────────────────────────────────────────────

// ── Resize constants ────────────────────────────────────────────────────────
const NAV_MIN_WIDTH = 220;
const NAV_MAX_WIDTH = 600;
const NAV_DEFAULT_WIDTH = 357;

export default function WorkspaceNav({ onFileSelect, selectedFileKey, isCollapsed, onToggle, recentFiles, onRemoveRecent }: WorkspaceNavProps) {
  const { t } = useTranslation('workspace');
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | typeof LOAD_FAILED | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  /** Set of expanded folder paths */
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  /** Whether the Recent section is expanded */
  const [recentExpanded, setRecentExpanded] = useState(true);

  // ── Drag-to-resize (session-only — resets to default on page refresh) ─────
  const [navWidth, setNavWidth] = useState<number>(NAV_DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const navWidthRef = useRef(navWidth);
  navWidthRef.current = navWidth;
  const resizeCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => { resizeCleanupRef.current?.(); }, []);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    // Clean up any prior session whose mouseup was missed (e.g. focus loss)
    resizeCleanupRef.current?.();
    const startX = e.clientX;
    const startWidth = navWidthRef.current;

    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      const delta = ev.clientX - startX;
      const newWidth = Math.min(Math.max(startWidth + delta, NAV_MIN_WIDTH), NAV_MAX_WIDTH);
      setNavWidth(newWidth);
    };

    const cleanup = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      resizeCleanupRef.current = null;
    };

    const onMouseUp = () => cleanup();

    resizeCleanupRef.current = cleanup;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const handleResizeDoubleClick = useCallback(() => {
    setNavWidth(NAV_DEFAULT_WIDTH);
  }, []);

  // Map from file key → WorkspaceFile for resolving recent clicks
  const fileIndexRef = useRef<Map<string, WorkspaceFile>>(new Map());
  // Whether workspace has successfully loaded at least once (distinguishes "empty" from "not loaded")
  const hasLoadedRef = useRef(false);
  // Synchronous flag: true while loadAll() is in-flight (prevents stale-data navigation race)
  const isLoadingRef = useRef(false);

  // Context: refresh trigger + pending navigate
  const { refreshTrigger, pendingNavigateKey, setPendingNavigateKey, refreshWorkspace } = useWorkspaceNav();
  const { effectiveUid, isImpersonating } = useImpersonation();
  const { currentAgent } = useAgent();

  // Ref to hold pending key so loadAll can access it synchronously after fetch
  const pendingKeyRef = useRef<string | null>(null);
  pendingKeyRef.current = pendingNavigateKey;

  // ── File matching helper ────────────────────────────────────────────────
  const findFileByKey = useCallback((targetKey: string, index: Map<string, WorkspaceFile>): { file: WorkspaceFile; matchedKey: string } | null => {
    // 1. Exact match
    const exact = index.get(targetKey);
    if (exact) return { file: exact, matchedKey: targetKey };

    // 2. Fuzzy: endsWith
    for (const [key, f] of index.entries()) {
      if (key.endsWith(targetKey) || targetKey.endsWith(key)) {
        return { file: f, matchedKey: key };
      }
    }

    // 3. Filename match
    const fileName = targetKey.split('/').pop() || '';
    if (fileName) {
      for (const [key, f] of index.entries()) {
        if (key.endsWith('/' + fileName) || key === fileName) {
          return { file: f, matchedKey: key };
        }
      }
    }

    return null;
  }, []);

  // ── Navigate to file helper ─────────────────────────────────────────────
  const navigateToFile = useCallback((matchedKey: string, file: WorkspaceFile) => {
    const segments = matchedKey.split('/');
    if (segments.length > 1) {
      const foldersToExpand = segments.slice(0, -1).map((_, i) =>
        segments.slice(0, i + 1).join('/') + '/'
      );
      setExpandedPaths(prev => {
        const next = new Set(prev);
        for (const f of foldersToExpand) next.add(f);
        return next;
      });
    }
    onFileSelect(file);
    setPendingNavigateKey(null);
  }, [onFileSelect, setPendingNavigateKey]);

  // ── Shared load function ────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      const resp = await gatewayClient.listWorkspace({
        agent: currentAgent,
        ...(isImpersonating && effectiveUid ? { user: effectiveUid } : {}),
        prefix: 'output/',
        recursive: true,
      });

      // Strip the output/ prefix from keys for display
      const OUTPUT_PREFIX = 'output/';
      const displayFiles = resp.files.map(f =>
        f.key.startsWith(OUTPUT_PREFIX)
          ? { ...f, key: f.key.slice(OUTPUT_PREFIX.length) }
          : f,
      );

      // Build file index for recent-click resolution
      const idx = new Map<string, WorkspaceFile>();
      for (const f of displayFiles) {
        idx.set(f.key, f);
      }
      fileIndexRef.current = idx;
      hasLoadedRef.current = true;

      const built = sortTree(buildTree(displayFiles));
      // Folders start collapsed, so only the top level shows. A refresh keeps
      // whatever the user expanded; opening a file expands its parents.
      setTree(built);

      // After fresh data is loaded, immediately try to navigate to pending file
      const pendingKey = pendingKeyRef.current;
      if (pendingKey) {
        const result = findFileByKey(pendingKey, idx);
        if (result) {
          // Use setTimeout(0) to let React finish the state updates from loadAll
          setTimeout(() => navigateToFile(result.matchedKey, result.file), 0);
        } else {
          // File truly doesn't exist — remove from recent list and clear pending
          onRemoveRecent?.(pendingKey);
          setPendingNavigateKey(null);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : LOAD_FAILED);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [findFileByKey, navigateToFile, setPendingNavigateKey, onRemoveRecent, effectiveUid, isImpersonating, currentAgent]);

  // ── Load on mount ───────────────────────────────────────────────────────
  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ── Re-fetch when refreshTrigger changes (from context) ─────────────────
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadAll();
    }
  }, [refreshTrigger, loadAll]);

  // ── Auto-navigate: also handle case where data is already loaded ────────
  // (e.g. user clicks workspace link while WorkspaceNav already has fresh data)
  // Skip if loadAll() is in-flight — it will handle navigation after fresh data arrives.
  useEffect(() => {
    if (!pendingNavigateKey || loading || isLoadingRef.current) return;

    const result = findFileByKey(pendingNavigateKey, fileIndexRef.current);
    if (result) {
      navigateToFile(result.matchedKey, result.file);
    }
    // If not found, don't clear — loadAll will handle it after refresh completes
  }, [pendingNavigateKey, loading, findFileByKey, navigateToFile]);

  // ── Toggle folder expand/collapse (purely local) ──────────────────────────

  const toggleFolder = useCallback((folderPath: string) => {
    if (searchQuery) return; // ignore toggles during search
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(folderPath)) {
        next.delete(folderPath);
      } else {
        next.add(folderPath);
      }
      return next;
    });
  }, [searchQuery]);

  // ── Filter nodes by search ────────────────────────────────────────────────

  const displayedTree = useMemo(
    () => filterTree(tree, searchQuery),
    [tree, searchQuery],
  );

  // ── Auto-expand folders containing search matches ─────────────────────────

  const effectiveExpanded = useMemo(() => {
    if (!searchQuery) return expandedPaths;
    const auto = new Set(expandedPaths);
    function collectFolders(nodes: TreeNode[]) {
      for (const n of nodes) {
        if (n.isFolder) {
          auto.add(n.path);
          collectFolders(n.children);
        }
      }
    }
    collectFolders(displayedTree);
    return auto;
  }, [searchQuery, displayedTree, expandedPaths]);

  // ── Handle recent file click ──────────────────────────────────────────────

  const handleRecentClick = useCallback((entry: RecentEntry) => {
    const file = fileIndexRef.current.get(entry.key);
    if (file) {
      onFileSelect(file);
    } else {
      // File not in current index — refresh workspace and auto-navigate after reload
      setPendingNavigateKey(entry.key);
      refreshWorkspace();
    }
  }, [onFileSelect, setPendingNavigateKey, refreshWorkspace]);

  // ── Prune stale recent entries after workspace loads ────────────────────────
  // Only prune once when loading transitions from true → false (workspace just loaded).
  // Do NOT re-run when recentFiles changes — that causes newly-added entries to be
  // immediately pruned before the index is aware of them.

  const prevLoadingRef = useRef(true);
  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;

    // Only prune on the transition: loading → not loading
    if (wasLoading && !loading && hasLoadedRef.current && recentFiles && recentFiles.length > 0) {
      const index = fileIndexRef.current;
      for (const entry of recentFiles) {
        if (!index.has(entry.key)) {
          onRemoveRecent?.(entry.key);
        }
      }
    }
  }, [loading, recentFiles, onRemoveRecent]);

  // ── Filter recent files by search query ───────────────────────────────────

  const displayedRecent = useMemo(() => {
    if (!recentFiles || recentFiles.length === 0) return [];
    const MAX_RECENT = 5;

    // Only filter after workspace has successfully loaded at least once
    const existingRecent = !loading && hasLoadedRef.current
      ? recentFiles.filter(r => fileIndexRef.current.has(r.key))
      : recentFiles;

    if (!searchQuery) return existingRecent.slice(0, MAX_RECENT);
    const lower = searchQuery.toLowerCase();
    return existingRecent.filter(r => r.name.toLowerCase().includes(lower)).slice(0, MAX_RECENT);
  }, [recentFiles, searchQuery, loading]);

  // ── Render tree node ──────────────────────────────────────────────────────

  function renderNode(node: TreeNode, depth: number = 0) {
    const isSelected = !node.isFolder && node.path === selectedFileKey;

    if (node.isFolder) {
      const isExpanded = effectiveExpanded.has(node.path);

      return (
        <div key={node.path} className="ws-nav__group">
          <button
            className={`ws-nav__folder${isExpanded ? ' ws-nav__folder--expanded' : ''}`}
            style={{ paddingLeft: `${12 + depth * 16}px` }}
            onClick={() => toggleFolder(node.path)}
            aria-expanded={isExpanded}
          >
            <span className="ws-nav__folder-icon">
              {isExpanded ? '▼' : '▶'}
            </span>
            <span className="ws-nav__folder-name">{node.name}</span>
          </button>
          {isExpanded && node.children.length > 0 && (
            <div className="ws-nav__children">
              {node.children.map(child => renderNode(child, depth + 1))}
            </div>
          )}
          {isExpanded && node.children.length === 0 && (
            <div className="ws-nav__empty" style={{ paddingLeft: `${28 + depth * 16}px` }}>
              {searchQuery ? t('nav.noMatches') : t('nav.emptyFolder')}
            </div>
          )}
        </div>
      );
    }

    // File node
    return (
      <button
        key={node.path}
        className={`ws-nav__file${isSelected ? ' ws-nav__file--selected' : ''}`}
        style={{ paddingLeft: `${28 + depth * 16}px` }}
        onClick={() => node.file && onFileSelect(node.file)}
        title={node.name}
      >
        <span className="ws-nav__file-name">{node.name}</span>
      </button>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (isCollapsed) {
    return null;
  }

  return (
    <nav
      className="ws-nav"
      aria-label={t('nav.label')}
      style={{ width: navWidth }}
    >
      {/* Drag handle on right edge */}
      <div
        className="ws-nav__resize-handle"
        onMouseDown={handleResizeStart}
        onDoubleClick={handleResizeDoubleClick}
        title={t('nav.resize')}
        role="separator"
        aria-orientation="vertical"
      />
      {isResizing && <div className="ws-nav__resize-overlay" />}

      {/* Header */}
      <div className="ws-nav__header">
        <span className="ws-nav__title">{t('nav.title')}</span>
        <button
          className="ws-nav__collapse-btn"
          onClick={onToggle}
          title={t('nav.collapse')}
          aria-label={t('nav.collapseLabel')}
        >
          ‹
        </button>
      </div>

      {/* Search */}
      <div className="ws-nav__search">
        <div className="ws-nav__search-wrapper">
          <svg className="ws-nav__search-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.0667 8.93333L13 11.8667L11.8667 13L8.93333 10.0667C8.0111 10.7778 6.88889 11.1333 5.66667 11.1333C2.53333 11.1333 0 8.6 0 5.56667C0 2.53333 2.53333 0 5.56667 0C8.6 0 11.1333 2.53333 11.1333 5.56667C11.1333 6.88889 10.7778 8.0111 10.0667 8.93333ZM5.56667 1.6C3.41333 1.6 1.6 3.41333 1.6 5.56667C1.6 7.72 3.41333 9.53333 5.56667 9.53333C7.72 9.53333 9.53333 7.72 9.53333 5.56667C9.53333 3.41333 7.72 1.6 5.56667 1.6Z" fill="#656871"/>
          </svg>
          <input
            type="text"
            className="ws-nav__search-input"
            placeholder={t('nav.search')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            aria-label={t('nav.searchLabel')}
          />
        </div>
      </div>

      {/* Scrollable area: Recent + Tree */}
      <div className="ws-nav__tree">
        {/* Recent section */}
        {displayedRecent.length > 0 && !error && (
          <>
            <div className="ws-nav__group">
              <button
                className={`ws-nav__folder${recentExpanded ? ' ws-nav__folder--expanded' : ''}`}
                style={{ paddingLeft: '12px' }}
                onClick={() => setRecentExpanded(prev => !prev)}
                aria-expanded={recentExpanded}
              >
                <span className="ws-nav__folder-icon">
                  {recentExpanded ? '▼' : '▶'}
                </span>
                <span className="ws-nav__folder-name">{t('nav.recent')}</span>
              </button>
              {recentExpanded && (
                <div className="ws-nav__children">
                  {displayedRecent.map(entry => {
                    const isSelected = entry.key === selectedFileKey;
                    return (
                      <button
                        key={`recent-${entry.key}`}
                        className={`ws-nav__file${isSelected ? ' ws-nav__file--selected' : ''}`}
                        style={{ paddingLeft: '28px' }}
                        onClick={() => handleRecentClick(entry)}
                        disabled={loading}
                        title={entry.key}
                      >
                        <span className="ws-nav__file-name">{entry.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="ws-nav__divider" />
          </>
        )}

        {/* File tree */}
        {loading && (
          <div className="ws-nav__loading">{t('nav.loading')}</div>
        )}
        {error && (
          <div className="ws-nav__error">{error === LOAD_FAILED ? t('nav.loadFailed') : error}</div>
        )}
        {!loading && !error && displayedTree.length === 0 && (
          <div className="ws-nav__empty-root">{t('nav.empty')}</div>
        )}
        {!loading && !error && displayedTree.map(node => renderNode(node))}
      </div>
    </nav>
  );
}
